import { Box, Button, FormComponent, Text } from '@factorialco/deprecated-design-system'
import {
  BreakConfigurationFragment,
  TimePlanningPlannedBreakBreakTypeEnum as BreakType,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/Inputs/DayPicker'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { stringToDate } from 'modules/core/lib/dates'
import { weekdaysWithUserFirstDayOfWeek } from 'modules/core/lib/firstDayOfWeek'
import { formatSecondsToHM } from 'modules/core/lib/formatTime'
import i18n from 'modules/core/lib/i18n'

import type PlannedBreakForm from 'modules/shift_management/lib/forms/PlannedBreakForm'
import { DayConfigurationWithBreaksForm } from 'modules/work_schedules/hooks/useDayConfigurations'
import useWeekdaysName from 'modules/work_schedules/hooks/useWeekdaysName'
import type WorkSchedulePeriodForm from 'modules/work_schedules/lib/forms/WorkSchedulePeriodForm'

import FlexibleDayConfigurationInput from './FlexibleDayConfigurationInput'
import NormalText from './NormalText'

export interface Props {
  onSubmit: () => void
  periodFormRef: WorkSchedulePeriodForm
  dayConfigForms: DayConfigurationWithBreaksForm[]
  totalHours: number
  disabled?: boolean
  edit?: boolean
  onCopyFirstToAll: () => void
  onAddBreak: (
    breakConfiguration: BreakConfigurationFragment,
    breakType: BreakType,
    dayConfiguration: DayConfigurationWithBreaksForm
  ) => void
  onDeleteBreak: (dayConfiguration: DayConfigurationWithBreaksForm, form: PlannedBreakForm) => void
  submitText?: string
  canAddBreaks: boolean
}

const FlexiblePeriodForm = ({
  onSubmit,
  periodFormRef,
  dayConfigForms,
  totalHours,
  disabled,
  edit,
  onCopyFirstToAll,
  onAddBreak,
  onDeleteBreak,
  submitText = i18n.t('settings.work_schedule.period_section.submit'),
  canAddBreaks,
}: Props) => {
  const {
    access: { firstDayOfWeek, timeFormat },
  } = useCurrentSession()
  const { weekDaysName } = useWeekdaysName()

  const startDateValue = periodFormRef.get('start_date').value
  const endDateValue = periodFormRef.get('end_date').value

  const dayConfigByDay = (weekday: string) =>
    dayConfigForms.filter(
      (dayConfig) => dayConfig.dayConfiguration.get('weekday').value === weekday
    )

  const isAllEmpty = dayConfigForms.every(({ dayConfiguration: { isEmpty } }) => isEmpty)

  const isBreaksFormValid = dayConfigForms.every(({ dayConfiguration, breaks }) => {
    return breaks.every((form) =>
      form.isValid({ duration: dayConfiguration.duration(timeFormat ?? 24) })
    )
  })

  return (
    <>
      <Box flexDirection="row" gap="s16" paddingY="s8">
        <DayPicker
          hideYear
          field={periodFormRef.get('start_date')}
          label={i18n.t('settings.work_schedule.settings_tab.starting_from')}
          readonly={!edit}
          value={!edit ? startDateValue : ''}
          format="ui_dm_long"
        />

        <DayPicker
          hideYear
          field={periodFormRef.get('end_date')}
          label={i18n.t('common.ending_at')}
          minDate={stringToDate(periodFormRef.get('start_date').value, 'ui')}
          readonly={!edit}
          value={!edit ? endDateValue : ''}
          format="ui_dm_long"
        />
      </Box>
      <Box gap="s16">
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Text.Normal color="grey800" size="200" weight="medium">
            {i18n.t('settings.work_schedule.period_section.working_hours')}
          </Text.Normal>

          <Button onClick={onCopyFirstToAll} type="secondary" iconName="layers-front">
            {i18n.t('shift_management.templates.weeks.settings_copy')}
          </Button>
        </Box>
        <Box paddingY="s8">
          {weekdaysWithUserFirstDayOfWeek(firstDayOfWeek).map((weekday) => {
            // Flexible WorkSchedules could only have one DayConfiguration per Day
            const dayConfigForm = dayConfigByDay(weekday)[0]

            if (!dayConfigForm) return

            return (
              <Box paddingBottom="s16" key={weekday}>
                <FlexibleDayConfigurationInput
                  dayConfigForm={dayConfigForm}
                  dayName={weekDaysName[weekday] ?? ''}
                  onAddBreak={onAddBreak}
                  onDeleteBreak={onDeleteBreak}
                  canAddBreaks={canAddBreaks}
                />
              </Box>
            )
          })}
        </Box>
      </Box>
      <Box
        flexDirection="row"
        gap="s8"
        justifyContent="spaceBetween"
        paddingX="s24"
        paddingY="s12"
        background={edit ? 'grey100' : 'grey000White'}
        borderRadius={{
          all: 'abs008',
        }}
      >
        <NormalText text={i18n.t('settings.work_schedule.period_section.total')} />
        <NormalText text={formatSecondsToHM(totalHours)} />
      </Box>

      <FormComponent.Buttons>
        <Button onClick={onSubmit} disabled={disabled || !isBreaksFormValid || isAllEmpty}>
          {submitText}
        </Button>
      </FormComponent.Buttons>
    </>
  )
}

export default observer(FlexiblePeriodForm)
